import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Globe } from 'lucide-react';
import { useAdminEvents } from '@/hooks/useEvents';
import { formatDateBrussels } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useQueryClient } from '@tanstack/react-query';

export default function AdminEvents() {
    const { data: events, isLoading } = useAdminEvents();
    const queryClient = useQueryClient();

    const handleDelete = async (id) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) {
            const { error } = await supabase.from('events').delete().eq('id', id);
            if (error) {
                alert('Erreur lors de la suppression');
            } else {
                await queryClient.invalidateQueries({ queryKey: ['events'] });
            }
        }
    };

    const toggleStatus = async (event) => {
        const { error } = await supabase
            .from('events')
            .update({ is_published: !event.is_published })
            .eq('id', event.id);

        if (error) {
            alert('Erreur de mise à jour');
        } else {
            await queryClient.invalidateQueries({ queryKey: ['events'] });
        }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-serif font-bold text-gray-900">Événements</h1>
                <Link
                    to="/admin/events/new"
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                    <Plus size={20} />
                    Nouvel événement
                </Link>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                            <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Titre</th>
                            <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Type</th>
                            <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {isLoading ? (
                            <tr>
                                <td colSpan="5" className="px-6 py-8 text-center text-gray-500">Chargement...</td>
                            </tr>
                        ) : events?.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="px-6 py-8 text-center text-gray-500">Aucun événement.</td>
                            </tr>
                        ) : (
                            events?.map((event) => (
                                <tr key={event.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-bold text-gray-900">{event.title}</div>
                                        <div className="text-xs text-gray-500">{event.location}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        {formatDateBrussels(event.date)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
                                            {event.type}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <button
                                            onClick={() => toggleStatus(event)}
                                            className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors ${event.is_published
                                                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                                    : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                                }`}
                                        >
                                            {event.is_published ? 'Publié' : 'Brouillon'}
                                        </button>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex items-center justify-end gap-3">
                                            <Link
                                                to={`/agenda/${event.slug}`}
                                                target="_blank"
                                                className="text-gray-400 hover:text-primary-600"
                                                title="Voir en ligne"
                                            >
                                                <Globe size={18} />
                                            </Link>
                                            <Link
                                                to={`/admin/events/${event.id}/edit`}
                                                className="text-primary-600 hover:text-primary-800"
                                                title="Modifier"
                                            >
                                                <Edit size={18} />
                                            </Link>
                                            <button
                                                onClick={() => handleDelete(event.id)}
                                                className="text-red-500 hover:text-red-700"
                                                title="Supprimer"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
